import type { ReactNode } from 'react';
import { Pressable, StyleSheet, Text, TextInput, View } from 'react-native';
import { Icon } from '../index';

import type { useTheme } from './theme';
import type { entryStyles } from './styles/entryStyles';

// Editable workout-name row for the timeline logger header (extracted from
// TimelineLoggerScreen to keep it under the 500-line cap, LG-14 split).
// Behaviour-preserving: tapping the name (a 2-line display + ✎ pencil, showing
// the auto-suggestion when unset) swaps in an autofocused TextInput that commits
// on blur/submit. Uses the shared entryStyles bag the screen already computed.
export function WorkoutNameEditor({
  name,
  nameSuggestion,
  editing,
  onStartEdit,
  onStopEdit,
  onChangeName,
  submitting,
  trailing,
  styles,
  theme,
  t,
}: {
  name: string | undefined;
  nameSuggestion: string;
  editing: boolean;
  onStartEdit: () => void;
  onStopEdit: () => void;
  onChangeName: (text: string) => void;
  submitting: boolean;
  /** A control parked at the end of the name row — EQ-1's gym chip.
   *  ⛔ Rendered OUTSIDE the name Pressable on purpose: that Pressable carries
   *  an accessibilityLabel, and a label on a row collapses everything under it
   *  into one announcement (the PainDot regression of 2026-08-13). A sibling
   *  keeps its own voice. */
  trailing?: ReactNode;
  styles: ReturnType<typeof entryStyles>;
  theme: ReturnType<typeof useTheme>;
  t: (k: string, opts?: Record<string, unknown>) => string;
}) {
  if (editing) {
    return (
      <View style={styles.nameEditRow}>
        <TextInput
          value={name ?? ''}
          onChangeText={onChangeName}
          placeholder={t('mobile.logger.namePlaceholder')}
          placeholderTextColor={theme.palette.ink3}
          maxLength={120}
          style={styles.nameInput}
          returnKeyType="done"
          autoFocus
          onBlur={onStopEdit}
          onSubmitEditing={onStopEdit}
          editable={!submitting}
        />
      </View>
    );
  }
  return (
    <View style={local.row}>
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={t('mobile.logger.editNameA11y', {
          defaultValue: 'Edit workout name',
        })}
        onPress={onStartEdit}
        disabled={submitting}
        style={({ pressed }) => [
          styles.nameDisplayRow,
          local.nameCell,
          pressed && styles.nameDisplayPressed,
        ]}
      >
        <Text
          style={[styles.nameDisplayText, !name?.trim() && styles.nameDisplayAuto]}
          numberOfLines={2}
        >
          {name?.trim() ? name : nameSuggestion}
        </Text>
        {/* namePencil had paddingHorizontal:s.s2 beyond the row gap — preserved with View wrapper */}
        <View style={{ paddingHorizontal: theme.spacing.s2 }}>
          <Icon name="edit" size={theme.typography.sizes.md} color={theme.palette.brand500} />
        </View>
      </Pressable>
      {trailing}
    </View>
  );
}

const local = StyleSheet.create({
  row: { flexDirection: 'row', alignItems: 'center' },
  // The name yields, the control keeps its size — a squeezed chip loses its
  // ≥44 touch target, a shortened name loses nothing (it already truncates).
  nameCell: { flex: 1 },
});
